// Contexte d'authentification global — supporte JWT + Google (Emergent).
//
// SEC-002 (P4) — Le JWT d'accès est un cookie HttpOnly (`session_token`) posé
// par le backend, jamais accessible depuis JavaScript. Aucune persistance
// côté client dans localStorage : tout passe par les cookies avec
// `withCredentials: true` (cf. lib/api.js).
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITIQUE : si on revient d'Emergent OAuth, laisser AuthCallback gérer.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // SEC-002 : purge résiduelle d'anciens tokens stockés localement (migration).
    try { localStorage.removeItem("elia_token"); } catch { /* ignore */ }
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = async (email, password, remember_me = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me });
    // SEC-2FA — si le compte a 2FA activé, le backend ne renvoie PAS de session
    // mais un challenge_token éphémère. La session (cookie HttpOnly) est créée
    // uniquement après verify du code TOTP/backup via /auth/two-factor/verify.
    if (data?.two_factor_required && data?.challenge_token) {
      return {
        twoFactorRequired: true,
        challengeToken: data.challenge_token,
        challengeExpiresAt: data.challenge_expires_at,
        challengeMethod: data.challenge_method || "totp",
      };
    }
    // Cookie HttpOnly `session_token` déjà posé par le backend — rien à stocker.
    setUser(data.user);
    return data.user;
  };

  const verifyTwoFactorChallenge = async (challengeToken, code) => {
    const { data } = await api.post("/auth/two-factor/verify", {
      challenge_token: challengeToken, code,
    });
    setUser(data.user);
    return data.user;
  };

  const registerWithPassword = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {/* ignore */}
    // Le backend efface les cookies. On purge aussi les résidus locaux.
    try { localStorage.removeItem("elia_token"); } catch { /* ignore */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, checkAuth, loginWithPassword, verifyTwoFactorChallenge, registerWithPassword, loginWithGoogle, logout, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
